import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField } from "@/lib/crm";
import { fmtDate } from "@/lib/format";
import { DOC_KINDS, DOC_STATUS, DocStatus, Empty, options } from "@/lib/assets";
import { DemoBadge, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DocDialog } from "@/components/assets/DocDialog";
import { DocDetail } from "@/components/assets/DocDetail";

export const DocsList = ({ company }) => {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [flt, setFlt] = useState({ q: "", kind: "", status: "" });
  const [items, setItems] = useState(null);
  const [dlg, setDlg] = useState(null);
  const detail = params.get("open");
  const setDetail = (id) => setParams((p) => { const n = new URLSearchParams(p); if (id) n.set("open", id); else n.delete("open"); return n; }, { replace: true });
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries({ ...flt, company_id: company?.id }).filter(([, v]) => v));
    api.get("/commercial-documents", { params: p }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [flt, company?.id]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  return (
    <Panel className="p-4 md:p-5" data-testid="docs-list">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca titolo, numero, codice, ordine" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} data-testid="docs-search" /></div>
        <SelectField value={flt.kind} onChange={(v) => setFlt({ ...flt, kind: v })} options={options(DOC_KINDS)} allowNone="Tutti i tipi" placeholder="Tipo" testId="docs-filter-kind" className="w-44" />
        <SelectField value={flt.status} onChange={(v) => setFlt({ ...flt, status: v })} options={options(DOC_STATUS)} allowNone="Tutti gli stati" placeholder="Stato" testId="docs-filter-status" className="w-40" />
        {can("commercial_docs:create") && !company?.archived_at && <Button className="gap-1" onClick={() => setDlg({})} data-testid="doc-new"><Plus className="h-4 w-4" />Nuovo documento</Button>}
      </div>
      {items && !items.length && <Empty testId="docs-empty">Nessun documento commerciale registrato.</Empty>}
      <ul className="divide-y divide-line">{(items || []).map((d) => (
        <li key={d.id}><button type="button" onClick={() => setDetail(d.id)} className="flex w-full flex-wrap items-center gap-3 py-3 text-left hover:bg-slate-50/60" data-testid={`doc-row-${d.id}`}>
          <div className="min-w-0 flex-1"><p className="font-mono text-xs font-semibold text-slate-400">{d.code} · {DOC_KINDS[d.kind]}{d.number ? ` n. ${d.number}` : ""}{d.date ? ` del ${fmtDate(d.date)}` : ""}</p>
            <p className="truncate text-sm font-semibold text-ink">{d.title} {d.is_demo && <DemoBadge className="ml-1" />}</p>
            <p className="text-xs text-slate-500">{[!company && d.company_name, d.quote && "da offerta", `${d.active_links} collegamenti`].filter(Boolean).join(" · ")}</p></div>
          <DocStatus value={d.status} testId={`doc-status-${d.id}`} />
        </button></li>))}</ul>
      <DocDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} supersede={dlg?.supersede} presetCompany={company ? { id: company.id, name: company.name } : null}
        onSaved={(id) => { load(); setDetail(id); }} />
      <DocDetail id={dlg ? null : detail} onOpenChange={(o) => !o && setDetail(null)} onChanged={load} onSupersede={(d) => setDlg({ supersede: d })} />
    </Panel>
  );
};
